import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useMsal } from '@azure/msal-react';
import { ArrowRight } from 'lucide-react';
import DefaultMediaFallback from '../../components/common/DefaultMediaFallback';
import HobtalkLoader from '../../components/common/HobtalkLoader';
import { hobbyMediaClassName, HobbyMediaIcon, normalizeHobbyMediaId } from '../../content/hobbyMedia';
import { hobbyData } from '../hobbies/HobbySidebar';
import { DISMISS_INSIGHT_RECAP_PROMPT, GET_HOME_FEED_PAGE, GET_HOME_PROFILE, GET_HOME_SUMMARY, MARK_INSIGHT_RECAP_SHARED } from './operations';
import { GET_MY_PROFILE, GET_MY_SUBSCRIPTION_STATUS, UPDATE_MY_PROFILE, UPLOAD_PROFILE_PHOTO } from '../account/operations';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import { getPublicErrorMessage, isLimitReachedError } from '../../utils/errors';
import {
  commonStrings,
  feedTypeLabels,
  homeStrings,
  mediaStrings,
  moduleCommonStrings,
  starterHobbies,
  starterIdeas,
} from '../../content';
import { normalizeCollectionOptions, includesCollectionName } from '../../utils/collectionOptions';
import { ADD_MOVIE, GET_MOVIES } from '../../features/movies/operations';
import { ADD_TV_SHOW, GET_TV_SHOWS } from '../../features/tvshows/operations';
import { ADD_BOOK, GET_BOOKS } from '../../features/reading/operations';
import InsightsRecapShareModal from '../../components/shareHighlights/InsightsRecapShareModal';

const allowedPhotoTypes = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Retrieves file as base64.
 *
 * @param {*} file - file value.
 * @returns {*} The derived operation result.
 */
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = () => reject(new Error(homeStrings.profile.readFailed));
    reader.readAsDataURL(file);
  });
}

/**
 * Renders the feed card experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.item - item value.
 * @returns {JSX.Element} The rendered interface.
 */
function FeedCard({ item }) {
  const href = homeTargetHref(item);
  const actionLabel = item?.action?.label || item.actionLabel || homeStrings.feed.open;
  const mediaUrl = item?.media?.url;

  return (
    <Link className={`personal-feed-card feed-type-${item.type || 'default'}`} to={href}>
      <div className="feed-card-media" aria-hidden="true">
        {mediaUrl ? <img src={mediaUrl} alt="" loading="lazy" /> : <MediaFallback item={item} />}
      </div>
      <div>
        <div className="feed-card-topline">
          <span>{item.hobby}</span>
        </div>
        <div>
          <h4>{item.title}</h4>
          <p>{item.subtitle || item.reason || feedTypeLabels[item.type] || homeStrings.feed.recommended}</p>
        </div>
        <div className="feed-card-footer">
          <strong>{actionLabel}</strong>
          <span aria-hidden="true">›</span>
        </div>
      </div>
    </Link>
  );
}

/**
 * Renders the recommendation card experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.item - item value.
 * @param {*} props.onOpen - Callback invoked when open occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function RecommendationCard({ item, onOpen }) {
  const mediaUrl = item?.media?.url;
  const detail = getRecommendationCardDetail(item);
  const isPosterCard = isMediaRecommendation(item);

  return (
    <button
      type="button"
      className={`home-recommendation-card ${isPosterCard ? 'poster-card' : 'idea-card'} ${isBookRecommendation(item) ? 'book-card' : ''}`}
      onClick={() => onOpen(item)}
    >
      {isPosterCard ? (
        <>
          <div className="home-recommendation-media poster-media" aria-hidden="true">
            {mediaUrl ? <img src={mediaUrl} alt="" loading="lazy" /> : <MediaFallback item={item} />}
          </div>
          <div className="home-recommendation-copy">
            <h4>{item.title}</h4>
            {detail && <p>{detail}</p>}
          </div>
        </>
      ) : (
        <>
          <IdeaFallbackStrip item={item} detail={detail} />
          <div className="home-recommendation-copy">
            <span>{ideaKindLabel(item)}</span>
            <h4>{displayRecommendationTitle(item)}</h4>
            {detail && <p>{detail}</p>}
          </div>
        </>
      )}
    </button>
  );
}

/**
 * Renders the discovery detail modal experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.item - item value.
 * @param {*} props.subscription - subscription value.
 * @param {*} props.onClose - Callback invoked when close occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function DiscoveryDetailModal({ item, subscription, onClose }) {
  if (!item) return null;
  const href = homeTargetHref(item);
  const mediaUrl = item?.media?.url;
  const isPosterCard = isMediaRecommendation(item);
  const actionLabel = item.hobbyId === 'cooking' && item.actionType === 'add_recipe'
    ? homeStrings.feed.openCooking
    : item.actionLabel || item?.action?.label || commonStrings.actions.open;
  const body = item.body && item.body !== item.reason ? item.body : '';
  const detailLabel = ['add_movie_to_watchlist', 'add_tv_to_watchlist', 'add_book_to_reading_list'].includes(item.actionType)
    ? homeStrings.feed.mediaDetailLabel
    : homeStrings.feed.ideaDetailLabel;

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="home-discovery-modal" role="dialog" aria-modal="true" aria-labelledby="home-discovery-title" onMouseDown={(event) => event.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label={commonStrings.actions.close}>×</button>
        <div className={`home-discovery-modal-media ${isPosterCard ? 'poster-detail' : 'idea-detail'}`} aria-hidden="true">
          {mediaUrl && isPosterCard ? <img src={mediaUrl} alt="" /> : <MediaFallback item={item} />}
        </div>
        <div className="home-discovery-modal-copy">
          <span>{isPosterCard ? item.hobby : ideaKindLabel(item)}</span>
          <h3 id="home-discovery-title">{displayRecommendationTitle(item)}</h3>
          {item.subtitle && <p>{item.subtitle}</p>}
          {body && (
            <div className="home-discovery-modal-detail">
              <strong>{detailLabel}</strong>
              <p>{body}</p>
            </div>
          )}
          {isMediaRecommendation(item) && item.actionPayload ? (
            <RecommendationSaveControl item={item} subscription={subscription} onClose={onClose} />
          ) : (
            <Link className="btn-primary" to={href} state={homeRecommendationState(item)} onClick={onClose}>{actionLabel}</Link>
          )}
        </div>
      </section>
    </div>
  );
}

/**
 * Renders the recommendation save control experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.item - item value.
 * @param {*} props.subscription - subscription value.
 * @param {*} props.onClose - Callback invoked when close occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function RecommendationSaveControl({ item, subscription, onClose }) {
  const config = useMemo(() => recommendationSaveConfig(item), [item]);
  const { data } = useQuery(config.query, {
    variables: { first: 50, after: null },
    skip: !config.query,
    fetchPolicy: 'cache-and-network',
  });
  const options = useMemo(() => normalizeCollectionOptions(config.collections(data)), [config, data]);
  const [mode, setMode] = useState(options.length ? 'existing' : 'new');
  const [collection, setCollection] = useState(options[0] || config.defaultCollection);
  const [error, setError] = useState('');
  const [didSave, setDidSave] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [saveItem, { loading }] = useMutation(config.mutation);

  useEffect(() => {
    if (!options.length) {
      setMode('new');
      setCollection('');
      return;
    }
    setMode((current) => (current === 'new' ? current : 'existing'));
    setCollection((current) => (includesCollectionName(options, current) ? current : options[0]));
  }, [options]);

  const cleanCollection = collection.trim() || config.defaultCollection;
  const isDuplicate = mode === 'new' && includesCollectionName(options, collection);

  /**
   * Handles save.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSave = async () => {
    if (isDuplicate) {
      setError(config.duplicateMessage);
      return;
    }
    setError('');
    try {
      await saveItem({ variables: { input: config.toInput(item, cleanCollection) } });
      setDidSave(true);
      onClose();
    } catch (saveError) {
      if (isLimitReachedError(saveError)) {
        setShowUpsell(true);
        return;
      }
      setError(getPublicErrorMessage(saveError));
    }
  };

  return (
    <div className="recommendation-save-control">
      <label>{config.label}</label>
      {options.length > 0 && (
        <div className="collection-mode-toggle" role="group" aria-label={`${config.label} mode`}>
          <button
            type="button"
            className={mode === 'existing' ? 'active' : ''}
            onClick={() => {
              setMode('existing');
              setCollection(includesCollectionName(options, collection) ? collection : options[0]);
              setError('');
            }}
          >
            {moduleCommonStrings.actions.useExisting}
          </button>
          <button
            type="button"
            className={mode === 'new' ? 'active' : ''}
            onClick={() => {
              setMode('new');
              setCollection('');
              setError('');
            }}
          >
            {moduleCommonStrings.actions.createNew}
          </button>
        </div>
      )}
      {mode === 'existing' && options.length > 0 ? (
        <select value={includesCollectionName(options, collection) ? collection : options[0]} onChange={(event) => setCollection(event.target.value)}>
          {options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : (
        <input value={collection} onChange={(event) => { setCollection(event.target.value); setError(''); }} placeholder={config.placeholder} />
      )}
      {error && <p className="field-error">{error}</p>}
      <button type="button" className="btn-primary" onClick={handleSave} disabled={loading || didSave}>
        {loading ? moduleCommonStrings.actions.saving : config.actionLabel}
      </button>
      {showUpsell && (
        <PremiumUpsellModal
          hobbyId={item.hobbyId}
          subscription={subscription}
          onChanged={async () => {
            setShowUpsell(false);
            await handleSave();
          }}
          onClose={() => setShowUpsell(false)}
        />
      )}
    </div>
  );
}

/**
 * Performs the recommendation save config operation.
 *
 * @param {*} item - item value.
 * @returns {*} The derived operation result.
 */
function recommendationSaveConfig(item) {
  if (item.actionType === 'add_tv_to_watchlist') {
    return {
      label: mediaStrings.tvshows.form.fields.collection,
      placeholder: mediaStrings.tvshows.form.placeholders.collection,
      defaultCollection: 'My Shows',
      actionLabel: item.actionLabel || 'Add to watchlist',
      duplicateMessage: mediaStrings.tvshows.form.errors.collectionDuplicate,
      query: GET_TV_SHOWS,
      mutation: ADD_TV_SHOW,
      collections: (data) => data?.getTVShowsListPage?.nodes?.map((show) => show.collection) || [],
      toInput: (recommendation, collection) => {
        const payload = recommendation.actionPayload || {};
        return {
          tvdbId: payload.tvdbId || recommendation.externalId || '',
          title: payload.title || recommendation.title || '',
          overview: payload.overview || recommendation.body || '',
          posterUrl: payload.posterUrl || recommendation.media?.url || '',
          firstAirYear: payload.firstAirYear || '',
          genres: Array.isArray(payload.genres) ? payload.genres : [],
          cast: Array.isArray(payload.cast) ? payload.cast : [],
          creator: payload.creator || '',
          network: payload.network || '',
          status: payload.productionStatus || '',
          userStatus: 'Want to watch',
          favorite: false,
          rating: '',
          review: '',
          spoiler: false,
          notes: '',
          collection,
          whereWatched: '',
          seasons: Array.isArray(payload.seasons) ? payload.seasons : [],
        };
      },
    };
  }
  if (item.actionType === 'add_book_to_reading_list') {
    return {
      label: mediaStrings.reading.form.fields.collection,
      placeholder: mediaStrings.reading.form.placeholders.collection,
      defaultCollection: 'My Books',
      actionLabel: item.actionLabel || 'Add to reading list',
      duplicateMessage: mediaStrings.reading.form.errors.collectionDuplicate,
      query: GET_BOOKS,
      mutation: ADD_BOOK,
      collections: (data) => data?.getBooksListPage?.nodes?.map((book) => book.collection) || [],
      toInput: (recommendation, collection) => {
        const payload = recommendation.actionPayload || {};
        return {
          catalogId: payload.catalogId || recommendation.externalId || '',
          title: payload.title || recommendation.title || '',
          subtitle: payload.subtitle || '',
          authors: Array.isArray(payload.authors) ? payload.authors : [],
          description: payload.description || recommendation.body || '',
          coverUrl: payload.coverUrl || recommendation.media?.url || '',
          publishedYear: payload.publishedYear || '',
          pageCount: Number(payload.pageCount || 0),
          genres: Array.isArray(payload.genres) ? payload.genres : [],
          shelf: 'Want to read',
          collection,
          format: 'Book',
          favorite: false,
          rating: '',
          review: '',
          spoiler: false,
          notes: '',
          progressType: 'Page',
          currentPage: 0,
          currentPercent: 0,
          currentMinutes: 0,
          startedDate: '',
          finishedDate: '',
          lastReadDate: '',
        };
      },
    };
  }
  return {
    label: mediaStrings.movies.form.fields.collection,
    placeholder: mediaStrings.movies.form.placeholders.collection,
    defaultCollection: 'My Movies',
    actionLabel: item.actionLabel || 'Add to watchlist',
    duplicateMessage: mediaStrings.movies.form.errors.collectionDuplicate,
    query: GET_MOVIES,
    mutation: ADD_MOVIE,
    collections: (data) => data?.getMoviesPage?.nodes?.map((movie) => movie.collection) || [],
    toInput: (recommendation, collection) => {
      const payload = recommendation.actionPayload || {};
      return {
        tmdbId: payload.tmdbId || recommendation.externalId || '',
        title: payload.title || recommendation.title || '',
        releaseYear: payload.releaseYear || '',
        posterUrl: payload.posterUrl || recommendation.media?.url || '',
        director: payload.director || '',
        cast: Array.isArray(payload.cast) ? payload.cast : [],
        genres: Array.isArray(payload.genres) ? payload.genres : [],
        runtime: payload.runtime || '',
        overview: payload.overview || recommendation.body || '',
        status: 'Want to watch',
        rating: '',
        favorite: false,
        watchedDate: '',
        rewatch: false,
        review: '',
        notes: '',
        spoiler: false,
        collection,
        whereWatched: '',
      };
    },
  };
}

const mediaRecommendationActions = new Set([
  'add_movie_to_watchlist',
  'add_tv_to_watchlist',
  'add_book_to_reading_list',
]);

/**
 * Performs the media fallback key operation.
 *
 * @param {*} item - item value.
 * @returns {*} The derived operation result.
 */
function mediaFallbackKey(item) {
  return String(item?.media?.fallback || item?.hobbyId || 'hobby').toLowerCase();
}

/**
 * Performs the fallback class name operation.
 *
 * @param {string} key - key value.
 * @returns {*} The derived operation result.
 */
function fallbackClassName(key) {
  return hobbyMediaClassName(key).replace(/^fallback-/, '');
}

/**
 * Determines whether media recommendation.
 *
 * @param {*} item - item value.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
function isMediaRecommendation(item) {
  return mediaRecommendationActions.has(item?.actionType);
}

/**
 * Determines whether book recommendation.
 *
 * @param {*} item - item value.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
function isBookRecommendation(item) {
  return item?.actionType === 'add_book_to_reading_list' || item?.hobbyId === 'reading' || item?.media?.fallback === 'book';
}

/**
 * Performs the display recommendation title operation.
 *
 * @param {*} item - item value.
 * @returns {*} The derived operation result.
 */
function displayRecommendationTitle(item) {
  const title = String(item?.title || '').trim();
  if (item?.hobbyId !== 'cooking') return title;
  return title.replace(/^(save|add|create)\s+(a|an|the)\s+/i, '').trim() || title;
}

/**
 * Retrieves recommendation card detail.
 *
 * @param {*} item - item value.
 * @returns {*} The derived operation result.
 */
function getRecommendationCardDetail(item) {
  if (isMediaRecommendation(item)) {
    return item?.subtitle || '';
  }
  return item?.subtitle || item?.reason || '';
}

/**
 * Performs the idea kind label operation.
 *
 * @param {*} item - item value.
 * @returns {*} The derived operation result.
 */
function ideaKindLabel(item) {
  switch (item?.hobbyId) {
    case 'cooking':
      return homeStrings.feed.recipeIdea || 'Recipe idea';
    case 'traveling':
      return homeStrings.feed.tripIdea || 'Trip idea';
    case 'fitness':
      return homeStrings.feed.fitnessIdea || 'Fitness idea';
    case 'hiking':
      return homeStrings.feed.trailIdea || 'Trail idea';
    default:
      return homeStrings.feed.idea || 'Idea';
  }
}

/**
 * Renders the media fallback experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.item - item value.
 * @param {*} props.showLabel - show label value.
 * @returns {JSX.Element} The rendered interface.
 */
function MediaFallback({ item, showLabel = true }) {
  const key = mediaFallbackKey(item);
  return <DefaultMediaFallback className={`home-media-fallback ${showLabel ? '' : 'icon-only'}`} fallbackType={fallbackClassName(key)} />;
}

/**
 * Renders the idea fallback strip experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.item - item value.
 * @param {*} props.detail - detail value.
 * @returns {JSX.Element} The rendered interface.
 */
function IdeaFallbackStrip({ item, detail }) {
  const key = mediaFallbackKey(item);
  const mediaId = normalizeHobbyMediaId(key);
  const titleLength = displayRecommendationTitle(item).length;
  const detailLength = String(detail || '').trim().length;
  const compactClass = detailLength === 0 && titleLength <= 34 ? 'tall' : detailLength > 70 ? 'short' : 'medium';
  return (
    <span className={`home-idea-fallback-strip ${compactClass} fallback-${fallbackClassName(key)}`} aria-hidden="true">
      <HobbyMediaIcon type={mediaId} />
    </span>
  );
}

/**
 * Performs the media fallback label operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function mediaFallbackLabel(value) {
  return String(value || 'H').trim().slice(0, 1).toUpperCase();
}

/**
 * Performs the home target href operation.
 *
 * @param {*} item - item value.
 * @returns {*} The derived operation result.
 */
function homeTargetHref(item) {
  if (item?.action?.href) return item.action.href;
  if (item?.actionHref) return item.actionHref;
  const targetId = item?.action?.targetId || item?.targetId;
  const targetType = item?.action?.targetType || item?.targetType;
  if (targetId) {
    if (targetType === 'trip') return `/hobby/traveling?tripId=${encodeURIComponent(targetId)}`;
    return `/hobby/${item.hobbyId}?itemId=${encodeURIComponent(targetId)}&itemType=${encodeURIComponent(targetType || '')}`;
  }
  return `/hobby/${item.hobbyId}`;
}

/**
 * Performs the home recommendation state operation.
 *
 * @param {*} item - item value.
 * @returns {*} The derived operation result.
 */
function homeRecommendationState(item) {
  if (!item?.actionPayload) return undefined;
  if (!['add_movie_to_watchlist', 'add_tv_to_watchlist', 'add_book_to_reading_list'].includes(item.actionType)) return undefined;
  return {
    homeRecommendation: {
      id: item.id,
      actionType: item.actionType,
      hobbyId: item.hobbyId,
      title: item.title,
      subtitle: item.subtitle,
      body: item.body,
      reason: item.reason,
      externalId: item.externalId,
      provider: item.provider,
      media: item.media,
      actionPayload: item.actionPayload,
    },
  };
}

/**
 * Renders the first time home experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.feedItems - feed items collection.
 * @param {*} props.profile - Profile used by the shared photo-onboarding card.
 * @param {boolean} props.isProfileLoading - Whether the profile is still loading.
 * @param {Function} props.onUploadPhoto - Uploads a selected profile photo.
 * @param {Function} props.onSkipOnboarding - Dismisses the photo prompt.
 * @param {boolean} props.isUploadingPhoto - Whether a profile photo upload is in progress.
 * @returns {JSX.Element} The rendered interface.
 */
function FirstTimeHome({ feedItems = [], profile, isProfileLoading, onUploadPhoto, onSkipOnboarding, isUploadingPhoto }) {
  const featuredHobbies = hobbyData.slice(0, 4).map((hobby) => ({
    ...hobby,
    prompt: starterHobbies.find((item) => item.hobbyId === hobby.id),
  }));
  const starterFeedItems = feedItems.length ? feedItems : starterIdeas.map((item, index) => ({
    id: `starter_${index}_${item.hobbyId}`,
    type: 'popular',
    hobbyId: item.hobbyId,
    hobby: item.meta,
    title: item.title,
    body: item.note,
    reason: feedTypeLabels.popular,
    actionLabel: commonStrings.actions.open,
    priority: commonStrings.priority.low,
  }));

  return (
    <div className="home-page first-time-home">
      <main className="home-feed">
        {!isProfileLoading && profile && !profile.photoUrl && !profile.hasCompletedOnboarding && (
          <ProfilePhotoOnboarding
            profile={profile}
            isUploading={isUploadingPhoto}
            onUpload={onUploadPhoto}
            onSkip={onSkipOnboarding}
          />
        )}
        <section className="home-today-panel first-time-hero">
          <div className="today-copy">
            <p className="eyebrow">{homeStrings.firstTime.eyebrow}</p>
            <h1>{homeStrings.firstTime.title}</h1>
            <p>{homeStrings.firstTime.detail}</p>
            <div className="today-actions">
              <Link className="btn-primary" to="/hobbies">{homeStrings.firstTime.primaryAction}</Link>
            </div>
          </div>
          <div className="first-time-steps">
            {homeStrings.firstTime.steps.map((step, index) => (
              <div key={step}>
                <strong>{index + 1}</strong>
                <span>{step}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="feed-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{homeStrings.firstTime.firstEntryEyebrow}</p>
              <h3>{homeStrings.firstTime.firstEntryTitle}</h3>
            </div>
            <span>{commonStrings.counts.hobbies(hobbyData.length)}</span>
          </div>
          <div className="starter-grid">
            {featuredHobbies.map((hobby) => {
              const { prompt } = hobby;
              return (
                <Link
                  className="starter-card"
                  key={hobby.id}
                  style={{ '--hobby-theme': hobby.theme }}
                  to={`/hobby/${hobby.id}`}
                >
                  <span>{hobby.name}</span>
                  <h4>{prompt?.title || `Start with ${hobby.name}`}</h4>
                  <p>{prompt?.note || hobby.description}</p>
                  <strong>{prompt?.action || homeStrings.firstTime.primaryAction}</strong>
                </Link>
              );
            })}
          </div>
          <Link className="starter-all-link" to="/hobbies">
            {homeStrings.firstTime.allHobbiesAction}
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
        </section>

        <section className="feed-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{homeStrings.firstTime.starterIdeasEyebrow}</p>
              <h3>{homeStrings.firstTime.starterIdeasTitle}</h3>
            </div>
            <span>{commonStrings.counts.ideas(starterFeedItems.length)}</span>
          </div>
          <div className="personal-feed starter-feed">
            {starterFeedItems.map((item) => (
              <FeedCard item={item} key={item.id || item.title} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

/**
 * Normalizes relative date.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function formatRelativeDate(value) {
  if (!value) return homeStrings.dates.recently;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return homeStrings.dates.recently;
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (diffDays <= 0) return homeStrings.dates.today;
  if (diffDays === 1) return homeStrings.dates.yesterday;
  if (diffDays < 7) return homeStrings.dates.daysAgo(diffDays);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Determines whether timely feed item.
 *
 * @param {*} item - item value.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
function isTimelyFeedItem(item) {
  const type = String(item?.type || '').toLowerCase();
  const priority = String(item?.priority || '').toLowerCase();
  return ['plan', 'upcoming', 'continue', 'complete', 'decide'].includes(type) || ['high', 'urgent'].includes(priority);
}

/**
 * Renders the feed empty state experience.
 *
 * @returns {JSX.Element} The rendered interface.
 */
function FeedEmptyState() {
  return (
    <div className="home-empty-panel">
      <h4>{homeStrings.empty.feedTitle}</h4>
      <p>{homeStrings.empty.feedDetail}</p>
      <Link to="/hobbies">{homeStrings.error.openHobbies}</Link>
    </div>
  );
}

/**
 * Renders the recent empty state experience.
 *
 * @returns {JSX.Element} The rendered interface.
 */
function RecentEmptyState() {
  return (
    <div className="home-empty-panel compact">
      <h4>{homeStrings.empty.recentTitle}</h4>
      <p>{homeStrings.empty.recentDetail}</p>
    </div>
  );
}

/**
 * Renders the home header experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.profile - profile value.
 * @param {*} props.isProfileLoading - Whether is profile loading is enabled.
 * @param {*} props.fallbackName - fallback name value.
 * @returns {JSX.Element} The rendered interface.
 */
function HomeHeader({ profile, isProfileLoading, fallbackName = '' }) {
  const displayName = profile?.displayName?.trim() || fallbackName.trim();
  const firstName = displayName.split(/\s+/)[0] || '';

  return (
    <header className="home-personal-header">
      <div>
        {isProfileLoading && !firstName ? (
          <div className="home-greeting-skeleton" aria-label={commonStrings.loading.profile} />
        ) : (
          <h1>{firstName ? homeStrings.profile.welcomeBackName(firstName) : homeStrings.profile.welcomeBack}</h1>
        )}
      </div>
    </header>
  );
}

/**
 * Renders the profile photo onboarding experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.profile - profile value.
 * @param {*} props.onUpload - Callback invoked when upload occurs.
 * @param {*} props.onSkip - Callback invoked when skip occurs.
 * @param {*} props.isUploading - Whether is uploading is enabled.
 * @returns {JSX.Element} The rendered interface.
 */
function ProfilePhotoOnboarding({ profile, onUpload, onSkip, isUploading }) {
  const [error, setError] = useState('');
  const displayName = profile?.displayName?.trim() || homeStrings.profile.fallbackName;

  /**
   * Handles file change.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setError('');
    if (!file) return;
    if (!allowedPhotoTypes.includes(file.type)) {
      setError(homeStrings.profile.unsupportedPhotoType);
      return;
    }
    try {
      const base64Data = await readFileAsBase64(file);
      await onUpload({ contentType: file.type, base64Data });
    } catch {
      setError(homeStrings.profile.uploadFailed);
    }
  };

  return (
    <section className="home-onboarding-card">
      <div className="home-onboarding-avatar">
        {profile?.photoUrl ? <img src={profile.photoUrl} alt="" /> : <span>{mediaFallbackLabel(displayName)}</span>}
      </div>
      <div>
        <p className="eyebrow">{homeStrings.profile.eyebrow}</p>
        <h3>{homeStrings.profile.title}</h3>
        <p>{homeStrings.profile.detail}</p>
        {error && <small className="error-copy">{error}</small>}
      </div>
      <div className="home-onboarding-actions">
        <label className="btn-primary">
          {isUploading ? homeStrings.profile.uploadingPhoto : homeStrings.profile.addPhoto}
          <input type="file" accept="image/jpeg,image/png,image/webp" disabled={isUploading} onChange={handleFileChange} />
        </label>
        <button type="button" className="btn-secondary" disabled={isUploading} onClick={onSkip}>{commonStrings.actions.skip}</button>
      </div>
    </section>
  );
}

/**
 * Renders the home loading state experience.
 *
 * @returns {JSX.Element} The rendered interface.
 */
function HomeLoadingState() {
  return (
    <div className="home-page">
      <main className="home-feed">
        <section className="home-today-panel loading-hero">
          <div className="today-copy">
            <HobtalkLoader label={homeStrings.loading.title} />
            <p className="eyebrow">{homeStrings.loading.eyebrow}</p>
            <h1>{homeStrings.loading.title}</h1>
            <p>{homeStrings.loading.detail}</p>
          </div>
          <div className="today-stats">
            {[1, 2, 3, 4].map((item) => <div className="skeleton-card" key={item} />)}
          </div>
        </section>
        <div className="personal-home-layout">
          <section className="feed-section">
            <div className="personal-feed">
              {[1, 2, 3].map((item) => <div className="personal-feed-card skeleton-feed-card" key={item} />)}
            </div>
          </section>
          <aside className="home-side-panel">
            <section className="feed-section">
              <div className="recent-activity-list">
                {[1, 2, 3].map((item) => <div className="skeleton-card" key={item} />)}
              </div>
            </section>
          </aside>
        </div>
      </main>
    </div>
  );
}

/**
 * Renders the home error state experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.onRetry - Callback invoked when retry occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function HomeErrorState({ onRetry }) {
  return (
    <div className="home-page">
      <main className="home-feed">
        <section className="home-today-panel">
          <div className="today-copy">
            <p className="eyebrow">{homeStrings.error.eyebrow}</p>
            <h1>{homeStrings.error.title}</h1>
            <p>{homeStrings.error.detail}</p>
            <div className="today-actions">
              <button type="button" className="btn-primary" onClick={onRetry}>{homeStrings.error.retry}</button>
              <Link className="btn-secondary" to="/hobbies">{homeStrings.error.openHobbies}</Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

const HOME_FEED_PAGE_SIZE = 6;

/**
 * Renders the recent activity card experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.item - item value.
 * @returns {JSX.Element} The rendered interface.
 */
function RecentActivityCard({ item }) {
  const href = item.href || homeTargetHref(item);

  return (
    <Link className="recent-activity-card" to={href}>
      <div className="recent-media" aria-hidden="true">
        {item.media?.url ? <img src={item.media.url} alt="" loading="lazy" /> : <MediaFallback item={item} showLabel={false} />}
      </div>
      <div>
        <span>{item.hobby}</span>
        <strong>{item.title}</strong>
        <small>{item.subtitle || item.time || formatRelativeDate(item.updatedAt)}</small>
      </div>
      <em aria-hidden="true">›</em>
    </Link>
  );
}

/**
 * Renders the discovery shelves experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.shelves - shelves value.
 * @param {*} props.onOpenItem - Callback invoked when open item occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function DiscoveryShelves({ shelves = [], onOpenItem }) {
  if (!shelves.length) return null;

  return (
    <section className="home-recommendations-section">
      <div className="home-discovery-heading">
        <span aria-hidden="true" />
        <h3>{homeStrings.returning.recommendationsTitle}</h3>
      </div>
      <div className="recommendation-section-stack">
        {shelves.map((section) => (
          <div className="recommendation-module" key={section.id}>
            <div className="recommendation-module-heading">
              <div>
                <h4>{section.hobby}</h4>
                {(section.subtitle || section.reason || section.title) && (
                  <p>{section.subtitle || section.reason || section.title}</p>
                )}
              </div>
            </div>
            <div className="recommendation-shelf" role="list" aria-label={`${section.hobby} ideas`}>
              {(section.items || []).map((item) => (
                <RecommendationCard item={item} key={item.id} onOpen={onOpenItem} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Renders the returning home experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.summary - summary value.
 * @param {*} props.discoveryShelves - discovery shelves value.
 * @param {*} props.subscription - subscription value.
 * @param {*} props.profile - profile value.
 * @param {*} props.isProfileLoading - Whether is profile loading is enabled.
 * @param {*} props.fallbackName - fallback name value.
 * @param {*} props.feedItems - feed items collection.
 * @param {*} props.feedPageInfo - feed page info value.
 * @param {*} props.isFeedLoading - Whether is feed loading is enabled.
 * @param {*} props.onLoadMoreFeed - Callback invoked when load more feed occurs.
 * @param {*} props.onUploadPhoto - Callback invoked when upload photo occurs.
 * @param {*} props.onSkipOnboarding - Callback invoked when skip onboarding occurs.
 * @param {*} props.isUploadingPhoto - Whether is uploading photo is enabled.
 * @returns {JSX.Element} The rendered interface.
 */
function ReturningHome({ summary, discoveryShelves, subscription, profile, isProfileLoading, fallbackName, feedItems, feedPageInfo, isFeedLoading, onLoadMoreFeed, onUploadPhoto, onSkipOnboarding, isUploadingPhoto }) {
  const [selectedDiscoveryItem, setSelectedDiscoveryItem] = useState(null);
  const [selectedRecapPrompt, setSelectedRecapPrompt] = useState(null);
  const [hiddenRecapIds, setHiddenRecapIds] = useState(() => new Set());
  const [dismissRecapPrompt] = useMutation(DISMISS_INSIGHT_RECAP_PROMPT, { refetchQueries: [{ query: GET_HOME_SUMMARY, variables: { limit: 6 } }] });
  const [markRecapShared] = useMutation(MARK_INSIGHT_RECAP_SHARED, { refetchQueries: [{ query: GET_HOME_SUMMARY, variables: { limit: 6 } }] });
  const upcomingCount = feedItems.filter((item) => item.type === 'upcoming' || item.type === 'plan').length;
  const stats = [
    { label: homeStrings.stats.entriesThisWeek, value: String(summary.entriesThisWeek ?? 0), icon: '✎' },
    { label: homeStrings.stats.activeHobbies, value: String(summary.activeHobbies ?? 0), icon: '▦' },
    { label: homeStrings.stats.upcomingPlans, value: String(upcomingCount), icon: '◷' },
    { label: homeStrings.stats.favoritesAdded, value: String(summary.favoritesAddedThisWeek ?? 0), icon: '♡' },
  ];
  const activityItems = summary.recentItems || [];
  const nextItems = feedItems.filter(isTimelyFeedItem).slice(0, 6);
  const latestUpdatedAt = activityItems
    .map((item) => item.updatedAt)
    .filter(Boolean)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
  const recapPrompt = summary.recapPrompt && !hiddenRecapIds.has(summary.recapPrompt.id) ? summary.recapPrompt : null;
  /**
   * Performs the hide recap operation.
   *
   * @param {string} recapId - recap id value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const hideRecap = (recapId) => setHiddenRecapIds((current) => new Set([...current, recapId]));
  /**
   * Handles dismiss recap.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleDismissRecap = async () => {
    if (!recapPrompt) return;
    hideRecap(recapPrompt.id);
    try {
      await dismissRecapPrompt({ variables: { recapId: recapPrompt.id } });
    } catch {
      setHiddenRecapIds((current) => {
        const next = new Set(current);
        next.delete(recapPrompt.id);
        return next;
      });
    }
  };
  /**
   * Handles recap shared.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleRecapShared = async () => {
    if (!selectedRecapPrompt) return;
    hideRecap(selectedRecapPrompt.id);
    const recapId = selectedRecapPrompt.id;
    setSelectedRecapPrompt(null);
    try { await markRecapShared({ variables: { recapId } }); } catch { /* The next Home refresh can offer it again. */ }
  };

  return (
    <div className="home-page">
      <main className="home-feed">
        <HomeHeader profile={profile} isProfileLoading={isProfileLoading} fallbackName={fallbackName} />
        {!isProfileLoading && profile && !profile.photoUrl && !profile.hasCompletedOnboarding && (
          <ProfilePhotoOnboarding
            profile={profile}
            isUploading={isUploadingPhoto}
            onUpload={onUploadPhoto}
            onSkip={onSkipOnboarding}
          />
        )}

        <section className="home-snapshot-section">
          <div className="section-heading snapshot-heading">
            <div>
              <p className="eyebrow">{homeStrings.returning.snapshotEyebrow}</p>
              <h3>{homeStrings.returning.snapshotTitle}</h3>
            </div>
            {latestUpdatedAt && <span>Updated {formatRelativeDate(latestUpdatedAt)}</span>}
          </div>
          <div className="today-stats">
            {stats.map((stat) => (
              <div key={stat.label}>
                <i aria-hidden="true">{stat.icon}</i>
                <div>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {recapPrompt && (
          <section className="home-recap-prompt">
            <button type="button" className="home-recap-dismiss" onClick={handleDismissRecap} aria-label={homeStrings.returning.recapDismiss}>×</button>
            <p className="eyebrow">{homeStrings.returning.recapEyebrow}</p>
            <h3>{recapPrompt.title}</h3>
            <p>{recapPrompt.subtitle}</p>
            <button type="button" className="btn-primary" onClick={() => setSelectedRecapPrompt(recapPrompt)}>{homeStrings.returning.recapAction}</button>
          </section>
        )}

        <DiscoveryShelves shelves={discoveryShelves || []} onOpenItem={setSelectedDiscoveryItem} />

        <section className="feed-section continue-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{homeStrings.returning.nextEyebrow}</p>
              <h3>{homeStrings.returning.nextTitle}</h3>
            </div>
            {latestUpdatedAt && <span>Updated {formatRelativeDate(latestUpdatedAt)}</span>}
          </div>
          <div className="personal-feed action-feed">
            {nextItems.length === 0 && <RecentEmptyState />}
            {nextItems.map((item) => (
              <FeedCard item={item} key={item.id} />
            ))}
            {isFeedLoading && nextItems.length === 0 && [1, 2].map((item) => (
              <div className="personal-feed-card skeleton-feed-card" key={item} />
            ))}
          </div>
          {feedPageInfo?.hasNextPage && (
            <div className="load-more-row">
              <button type="button" className="btn-secondary" onClick={onLoadMoreFeed} disabled={isFeedLoading}>
                {isFeedLoading ? commonStrings.loading.activity : commonStrings.actions.loadMore}
              </button>
            </div>
          )}
        </section>

        <section className="feed-section recent-timeline-section">
          <div className="section-heading compact-home-heading">
            <div>
              <p className="eyebrow">{homeStrings.returning.recentEyebrow}</p>
              <h3>{homeStrings.returning.recentTitle}</h3>
            </div>
          </div>
          <div className="recent-activity-list timeline-list">
            {activityItems.length === 0 && <RecentEmptyState />}
            {activityItems.map((item) => (
              <RecentActivityCard item={item} key={`${item.hobby}-${item.title}-${item.id || item.time}`} />
            ))}
          </div>
        </section>
      </main>
      <DiscoveryDetailModal item={selectedDiscoveryItem} subscription={subscription} onClose={() => setSelectedDiscoveryItem(null)} />
      {selectedRecapPrompt && <InsightsRecapShareModal initialSummary={null} initialWindow={selectedRecapPrompt.window} onClose={() => setSelectedRecapPrompt(null)} onShared={handleRecapShared} />}
    </div>
  );
}

/** Loads and renders personalized Home summary, feed, discovery shelves, and onboarding. */
export default function HomeDashboard() {
  const { instance, accounts } = useMsal();
  const { loading, error, data, refetch } = useQuery(GET_HOME_SUMMARY, {
    variables: { limit: HOME_FEED_PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const summary = data?.getHomeSummary;
  const { loading: isProfileLoading, data: profileData } = useQuery(GET_HOME_PROFILE, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: subscriptionData } = useQuery(GET_MY_SUBSCRIPTION_STATUS, { fetchPolicy: 'cache-and-network' });
  const [uploadProfilePhoto, { loading: isUploadingPhoto }] = useMutation(UPLOAD_PROFILE_PHOTO, {
    refetchQueries: [{ query: GET_MY_PROFILE }, { query: GET_HOME_PROFILE }],
  });
  const [updateMyProfile] = useMutation(UPDATE_MY_PROFILE, {
    refetchQueries: [{ query: GET_MY_PROFILE }, { query: GET_HOME_PROFILE }],
  });
  const [feedItems, setFeedItems] = useState([]);
  const [feedPageInfo, setFeedPageInfo] = useState({});
  const [loadFeedPage, { loading: isFeedLoading }] = useLazyQuery(GET_HOME_FEED_PAGE, {
    fetchPolicy: 'network-only',
  });
  const profile = profileData?.getMyProfile;
  const activeAccount = instance.getActiveAccount?.() || accounts[0];
  const accountDisplayName = activeAccount?.name || '';

  useEffect(() => {
    setFeedItems(summary?.cards || []);
    setFeedPageInfo(summary?.cardsPageInfo || {});
  }, [summary]);

  /**
   * Handles retry.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleRetry = () => {
    refetch();
  };

  /**
   * Handles load more feed.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleLoadMoreFeed = async () => {
    if (!feedPageInfo.hasNextPage || !feedPageInfo.endCursor) return;
    const result = await loadFeedPage({
      variables: { first: HOME_FEED_PAGE_SIZE, after: feedPageInfo.endCursor },
    });
    const page = result.data?.getHomeFeedPage;
    if (!page) return;
    setFeedItems((items) => {
      const existingIds = new Set(items.map((item) => item.id));
      return [...items, ...(page.nodes || []).filter((item) => !existingIds.has(item.id))];
    });
    setFeedPageInfo(page.pageInfo || {});
  };

  /**
   * Handles upload onboarding photo.
   *
   * @param {object} options - Behavioral options for the operation.
   * @param {*} options.contentType - content type value.
   * @param {*} options.base64Data - base64 data value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleUploadOnboardingPhoto = async ({ contentType, base64Data }) => {
    await uploadProfilePhoto({
      variables: { input: { contentType, base64Data } },
    });
  };

  /**
   * Handles skip onboarding.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSkipOnboarding = async () => {
    await updateMyProfile({
      variables: { input: { hasCompletedOnboarding: true } },
    });
  };

  if (loading && !summary) {
    return <HomeLoadingState />;
  }

  if (error && !summary) {
    return <HomeErrorState onRetry={handleRetry} />;
  }

  return summary?.totalEntries > 0
    ? (
      <ReturningHome
        summary={summary}
        discoveryShelves={summary.discoveryShelves || []}
        subscription={subscriptionData?.getMySubscriptionStatus}
        profile={profile}
        isProfileLoading={isProfileLoading}
        fallbackName={accountDisplayName}
        feedItems={feedItems}
        feedPageInfo={feedPageInfo}
        isFeedLoading={isFeedLoading}
        onLoadMoreFeed={handleLoadMoreFeed}
        onUploadPhoto={handleUploadOnboardingPhoto}
        onSkipOnboarding={handleSkipOnboarding}
        isUploadingPhoto={isUploadingPhoto}
      />
    )
    : (
      <FirstTimeHome
        feedItems={summary?.cards || []}
        profile={profile}
        isProfileLoading={isProfileLoading}
        onUploadPhoto={handleUploadOnboardingPhoto}
        onSkipOnboarding={handleSkipOnboarding}
        isUploadingPhoto={isUploadingPhoto}
      />
    );
}
